import { type ClientAndDataT } from '@ruminati/types_v2/client'
import { type OrganisationClientPrecheckResponseT } from '@ruminati/types_v2/organisation'
import {
  type OrganisationConnectionT,
  type OrganisationConnectionUpdateT,
} from '@ruminati/types_v2/organisation_connection'
import {
  RelationshipCreateT,
  RelationshipT,
  RelationshipUpdateT,
} from '@ruminati/types_v2/relationship'
import { HTTPError } from 'ky'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { CommonApiV2Instance } from '@/services/common'

import { useOrganisationStore, useOrgMembersStore } from './organisations'
import { useUsersStore } from './users'

export const usePartnerOrgStore = defineStore('partner-org', () => {
  const organisationStore = useOrganisationStore()
  const orgMembersStore = useOrgMembersStore()
  const userStore = useUsersStore()

  const clients = ref<Array<ClientAndDataT> | undefined>()

  function setClients(newClients: Array<ClientAndDataT>) {
    clients.value = newClients
  }

  function addClient(newClient: ClientAndDataT) {
    if (!clients.value) clients.value = []
    clients.value.push(newClient)
  }

  async function precheckCreateOrganisationForNewClient(
    email: string,
  ): Promise<OrganisationClientPrecheckResponseT> {
    if (!organisationStore.currentOrg) throw Error('No current org ID')
    return await CommonApiV2Instance.post(
      `organisations/${organisationStore.currentOrgId}/clients/precheck`,
      { json: { email } },
    ).json<OrganisationClientPrecheckResponseT>()
  }

  async function assignMemberToClient(options: {
    orgMemberId: string
    clientOrgId: string
    roleId: string
  }): Promise<
    | {
        success: true
        relationship: RelationshipT
      }
    | {
        success: false
        errorCode?: string
        context?: Record<string, unknown>
      }
  > {
    const body: RelationshipCreateT = {
      recipientType: 'User',
      recipientId: options.orgMemberId,
      isPending: false,
      roleId: options.roleId,
      resourceId: options.clientOrgId,
      resourceType: 'Organisation',
      revokedPermissions: false,
    }
    try {
      const data = await CommonApiV2Instance.post(
        `organisations/${organisationStore.currentOrgId}/relationships`,
        {
          json: body,
        },
      ).json<RelationshipT>()

      if (clients.value && orgMembersStore.orgMembers) {
        const client = clients.value.find((c) => c.client.id === options.clientOrgId)
        const orgMember = orgMembersStore.orgMembers?.find(
          (m) => m.recipient?.id === options.orgMemberId,
        )
        if (client && orgMember && orgMember.recipient) {
          client.staffRelationshipsToClientOrg.push(data)
          client.staffAssignedToClientOrg.push(orgMember.recipient)
        }

        if (client && userStore.profile?.id === options.orgMemberId) {
          userStore.relationships?.push(data)
          if (organisationStore.organisations?.findIndex((o) => o.id === client.client.id) === -1) {
            // @ts-expect-error this isn't the full org profile
            organisationStore.organisations?.push({ ...client.client, type: 'farm' })
          }
        }
      }

      return { success: true, relationship: data }
    } catch (err) {
      if (err instanceof HTTPError) {
        try {
          const parsedErr = (await err.response.json()) as {
            code?: string
            context?: Record<string, unknown>
          }
          return { success: false, errorCode: parsedErr.code, context: parsedErr.context }
        } catch (parseError) {
          console.log(parseError)
        }
      }
      console.log(err)
      return { success: false }
    }
  }

  async function updateMembersRoleToClient(relationshipId: string, body: RelationshipUpdateT) {
    const response = await CommonApiV2Instance.patch(`relationships/${relationshipId}`, {
      json: body,
    }).json<RelationshipT>()
    if (response && clients.value) {
      clients.value.forEach((c) => {
        c.staffRelationshipsToClientOrg.forEach((r, idx) => {
          if (r.id === relationshipId) {
            c.staffRelationshipsToClientOrg.splice(idx, 1, response)
          }
        })
      })

      if (userStore.profile?.id === response.recipientId) {
        const relationshipIndex = userStore.relationships?.findIndex((r) => r.id === relationshipId)
        if (relationshipIndex && relationshipIndex > -1) {
          userStore.relationships?.splice(relationshipIndex, 1, response)
        }
      }
    }
  }

  async function acceptClient(connectionId: string) {
    const body: OrganisationConnectionUpdateT = {
      isPending: false,
      activatedDate: new Date().toISOString(),
    }
    const response = await CommonApiV2Instance.patch(`organisation-connections/${connectionId}`, {
      json: body,
    }).json<OrganisationConnectionT>()

    if (response && clients.value) {
      const client = clients.value.find((c) => c.clientRelationshipToPartnerOrg.id === connectionId)
      if (client) {
        client.clientRelationshipToPartnerOrg = response
      }
    }
  }

  async function archiveClient(connectionId: string) {
    const response = await CommonApiV2Instance.delete(`organisation-connections/${connectionId}`, {
      headers: {
        'content-type': undefined,
      },
    })

    if (response.status === 204 && clients.value) {
      const client = clients.value.find((c) => c.clientRelationshipToPartnerOrg.id === connectionId)
      if (client) {
        client.clientRelationshipToPartnerOrg.deletedAt = new Date().toISOString()
      }
    }
  }

  async function deleteMemberFromClient(relationshipId: string) {
    const response = await CommonApiV2Instance.delete(`relationships/${relationshipId}`, {
      headers: {
        'content-type': undefined,
      },
    })

    if (response.status === 204 && clients.value) {
      clients.value.forEach((c) => {
        c.staffRelationshipsToClientOrg.forEach((r, idx) => {
          if (r.id === relationshipId) {
            const staffIndex = c.staffAssignedToClientOrg.findIndex((s) => s.id === r.recipientId)
            if (staffIndex > -1) c.staffAssignedToClientOrg.splice(staffIndex, 1)
            c.staffRelationshipsToClientOrg.splice(idx, 1)

            if (userStore.profile?.id === r.recipientId) {
              const relationshipIndex = userStore.relationships?.findIndex(
                (r2) => r2.id === relationshipId,
              )
              if (relationshipIndex && relationshipIndex > -1) {
                userStore.relationships?.splice(relationshipIndex, 1)
              }
              const orgIndex = organisationStore.organisations?.findIndex(
                (o) => o.id === c.client.id,
              )
              if (orgIndex && orgIndex > -1) {
                organisationStore.organisations?.splice(orgIndex, 1)
              }
            }
          }
        })
      })
    }
  }

  const acceptedClients = computed(() => {
    return (clients.value ?? []).filter((c) => {
      return (
        !c.clientRelationshipToPartnerOrg.isPending && !c.clientRelationshipToPartnerOrg.deletedAt
      )
    })
  })

  const acceptedClientOrgIds = computed(() => {
    return acceptedClients.value.map((c) => c.client.id)
  })

  const pendingClients = computed(() => {
    return (clients.value ?? []).filter((c) => {
      return (
        c.clientRelationshipToPartnerOrg.isPending && !c.clientRelationshipToPartnerOrg.deletedAt
      )
    })
  })

  const acceptedClientsWithoutAllocatedStaff = computed(() => {
    return acceptedClients.value?.filter((c) => {
      return c.staffAssignedToClientOrg?.length === 0
    })
  })

  function reset() {
    clients.value = undefined
  }

  watch(
    () => organisationStore.currentOrgId,
    () => {
      reset()
    },
  )

  return {
    clients,
    setClients,
    addClient,
    reset,

    acceptedClients,
    pendingClients,
    acceptedClientOrgIds,
    acceptedClientsWithoutAllocatedStaff,

    precheckCreateOrganisationForNewClient,
    assignMemberToClient,
    updateMembersRoleToClient,
    acceptClient,
    archiveClient,
    deleteMemberFromClient,
  }
})
